import React from 'react';
import { ThemeProvider } from 'styled-components';
import nebulaTheme from '@nebula/theme';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { products } from '../../constants';

dayjs.extend(advancedFormat);
import {
  PageContainer,
  Title,
  Subtitle,
  SummaryCard,
  SummaryTitle,
  DetailsGrid,
  DetailLabel,
  DetailValue,
  CtaButton,
  SubtitleContent,
  Container,
  MainSections,
  TopSection,
  BottomSection,
  Header,
  AlertWrapper,
  Divider,
  RefundSection,
  RefundTitle,
  RefundBody,
} from './index.styles';
import { InformationAlert } from '@nebula/alert';
import { isMobileApp } from '@ecs-uk/web-common-mobile-view-helpers';
import usePageView from '../../hooks/usePageView';

interface ProductConfig {
  displayName: string;
  homepageUrl: string;
}

const PRODUCT_CONFIG: Record<string, ProductConfig> = {
  [products.identity]: {
    displayName: 'Identity Plus',
    homepageUrl: '/',
  },
  [products.credit_expert]: {
    displayName: 'CreditExpert',
    homepageUrl: '/',
  },
};

interface ThankYouProps {
  product: string;
  previousProduct?: string;
  hasFreeTrial: boolean;
  isTargetAnnual: boolean;
  isCurrentAnnual: boolean;
  price: string;
  proRataPrice?: string | null;
  refundAmount?: string | null;
  nextBillingDate: string;
  onCtaClick?: () => void;
}

const WHAT_HAPPENS_NEXT_MESSAGE =
  "Your benefits may take up to 24 hours to activate. If we cannot activate all of them, we'll give you a full refund within 5 working days.";

const HARDCODED_IDENTITY_ANNUAL_TO_CE_FIRST_PAYMENT = '£14.99';

const PAGE_NAME = 'ProdMoveNewThanksPage';

const ThankYou: React.FC<ThankYouProps> = ({
  product,
  previousProduct,
  hasFreeTrial,
  isTargetAnnual,
  isCurrentAnnual,
  price,
  proRataPrice,
  refundAmount,
  nextBillingDate,
  onCtaClick,
}) => {
  const config = PRODUCT_CONFIG[product] || PRODUCT_CONFIG[products.credit_expert];

  const parsedBillingDate = dayjs(nextBillingDate);
  const isValidBillingDate = parsedBillingDate.isValid();
  const formattedLongDate = isValidBillingDate ? parsedBillingDate.format('D MMMM YYYY') : nextBillingDate;
  const formattedCoverUntilDate = isValidBillingDate
    ? parsedBillingDate.subtract(1, 'day').format('D MMMM YYYY')
    : nextBillingDate;
  let formattedBillingDate = nextBillingDate;
  if (isValidBillingDate) {
    formattedBillingDate = isTargetAnnual
      ? parsedBillingDate.format('D MMMM [every year]')
      : parsedBillingDate.format('Do [of every month]');
  }

  const priceFrequency = isTargetAnnual ? 'year' : 'month';
  const displayPrice = `${price} / ${priceFrequency}`;

  const isPaidProduct = product === products.identity || product === products.credit_expert;
  const isFromFreeToPaid = previousProduct === products.basic && isPaidProduct;
  const isIdentityToCreditExpert = previousProduct === products.identity && product === products.credit_expert;
  const isIdentityAnnualToCreditExpert = isIdentityToCreditExpert && isCurrentAnnual;
  const isCreditExpertToIdentity = previousProduct === products.credit_expert && product === products.identity;
  const identityToCeSummaryPrice = proRataPrice || price;
  const normalizedIdentityToCeSummaryPrice = identityToCeSummaryPrice.trim().startsWith('£')
    ? identityToCeSummaryPrice
    : `£${identityToCeSummaryPrice}`;
  const subscriptionType = isTargetAnnual ? 'annual' : 'monthly';

  const getMovementCopy = (): { title: string; pageSubtitle: string } => {
    if (isCreditExpertToIdentity) {
      return {
        title: `You'll switch to Identity Plus on ${formattedLongDate}`,
        pageSubtitle: `You'll continue to enjoy your CreditExpert benefits until the ${formattedCoverUntilDate}. Your Identity Plus subscription and payments will start on ${formattedLongDate}.`,
      };
    }

    if (isIdentityToCreditExpert) {
      if (isIdentityAnnualToCreditExpert || hasFreeTrial) {
        return {
          title: `Welcome to ${config.displayName}`,
          pageSubtitle: `Your monthly subscription has started and we've taken your first payment of ${HARDCODED_IDENTITY_ANNUAL_TO_CE_FIRST_PAYMENT}.`,
        };
      }

      return {
        title: `Welcome to ${config.displayName}`,
        pageSubtitle: `Your monthly subscription has started and we've taken today's payment of ${normalizedIdentityToCeSummaryPrice}.`,
      };
    }

    if (isFromFreeToPaid) {
      return {
        title: `Welcome to ${config.displayName}`,
        pageSubtitle: hasFreeTrial
          ? `Your ${subscriptionType} subscription with a 30-day free trial has started.`
          : `Your ${subscriptionType} subscription has started.`,
      };
    }

    return {
      title: `Welcome to ${config.displayName}`,
      pageSubtitle: hasFreeTrial
        ? `Your ${subscriptionType} subscription with a 30-day free trial has started.`
        : `Your ${subscriptionType} subscription has started.`,
    };
  };

  const { title, pageSubtitle } = getMovementCopy();
  const ctaLabel = isCreditExpertToIdentity ? 'Go to your homepage' : `Start using ${config.displayName}`;
  const showRefundSection = isIdentityAnnualToCreditExpert;
  const showWhatHappensNext = !isCreditExpertToIdentity;
  const showBottomSection = showRefundSection || showWhatHappensNext;
  const isWebview = isMobileApp();
  const summaryDetails = isCreditExpertToIdentity
    ? [
        { label: 'Price:', value: displayPrice },
        { label: 'First payment:', value: `${price} on ${formattedLongDate}` },
        { label: 'Your billing date:', value: formattedBillingDate },
      ]
    : [
        { label: 'Price:', value: displayPrice },
        { label: 'Your billing date:', value: formattedBillingDate },
        { label: 'Next payment:', value: formattedLongDate },
      ];

  usePageView(PAGE_NAME);

  return (
    <ThemeProvider theme={nebulaTheme}>
      <Container $isWebview={isWebview}>
        <PageContainer $isWebview={isWebview}>
          <MainSections>
            <TopSection $hasRefundSection={showRefundSection} $isWebview={isWebview}>
              <Header>
                <Title>{title}</Title>
                <Subtitle>
                  <SubtitleContent>{pageSubtitle}</SubtitleContent>
                </Subtitle>
              </Header>

              <SummaryCard>
                <SummaryTitle>{`Your new ${subscriptionType} subscription`}</SummaryTitle>
                <DetailsGrid>
                  {summaryDetails.map(detail => (
                    <React.Fragment key={detail.label}>
                      <DetailLabel>{detail.label}</DetailLabel>
                      <DetailValue>{detail.value}</DetailValue>
                    </React.Fragment>
                  ))}
                </DetailsGrid>
              </SummaryCard>
            </TopSection>

            {showRefundSection && <Divider />}

            {showBottomSection && (
              <BottomSection>
                {showRefundSection && (
                  <RefundSection>
                    <RefundTitle>Your refund</RefundTitle>
                    <RefundBody>
                      {refundAmount
                        ? `We've cancelled your annual Identity Plus subscription and we'll refund ${refundAmount} within 5 working days.`
                        : `We've cancelled your annual Identity Plus subscription and we'll refund you within 5 working days.`}
                    </RefundBody>
                  </RefundSection>
                )}

                {showWhatHappensNext && (
                  <AlertWrapper>
                    <InformationAlert message={WHAT_HAPPENS_NEXT_MESSAGE} title="What happens next" />
                  </AlertWrapper>
                )}
              </BottomSection>
            )}
          </MainSections>

          <CtaButton buttonKind="primaryMagenta" label={ctaLabel} onClick={onCtaClick} />
        </PageContainer>
      </Container>
    </ThemeProvider>
  );
};

export default ThankYou;
